/**
 * Admin Authentication Guard
 * Protects admin routes - only allows crm_admin role
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AccessDenied } from './AccessDenied';
import { isCrmEnabled } from '../../utils/featureFlags';
import { CrmDisabled } from '../../pages/crm/CrmDisabled';

export const AdminAuthGuard = ({ children }) => {
  const { user, loading, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If not authenticated, redirect to admin login
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      navigate('/crm/admin/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  // Check if CRM is enabled
  if (!isCrmEnabled()) {
    return <CrmDisabled />;
  }

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Verify user is admin
  if (user && user.role !== 'crm_admin') {
    return (
      <AccessDenied
        requiredRole="crm_admin"
        userRole={user.role}
      />
    );
  }

  // User is authenticated and is admin
  return <>{children}</>;
};
